import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import objectPath from 'object-path';

import { ComponentRegistry } from 'core/index';
import { Field } from 'libs/forms/views/field';
import { Validation } from 'libs/forms/views/validation';
import {
  Selected,
  InputWrapper,
  WrappedInput,
  Placeholder,
  Arrow,
  ResetIcon,
  AutocompleteInputDivider as Divider,
} from 'libs/forms/styles';

import { StringFieldStore } from 'libs/forms/fieldStores';
import i18next from 'i18next';

export class SelectFieldBase extends Field {
  // TODO: turn into SelectComponent with "children"?

  componentDidMount() {
    if (this.props.fieldStore.autoFocus) {
      this._input.focus();
    }
    this.lastEvent = 'mouseDown';
  }

  onMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    if (this.lastEvent === 'focus') {
      this.lastEvent = 'mouseDown';
      if (this.props.fieldStore.optionsManager.showOptions) return;
    }
    if (!this.props.fieldStore.isFocussed) {
      this._input.focus();
    } else {
      this.props.fieldStore.optionsManager.ToggleShow();
    }
  };

  onKeyDown = () => {};
  onBlurSelect = e => {
    this.props.fieldStore.optionsManager.ToggleShow(false);
    if (this.props.fieldStore?.inputValue) {
      this.props.fieldStore.ClearInput();
    }

    this.onBlur(e);
  };

  onFocusSelect = e => {
    this.lastEvent = 'focus';
    setTimeout(() => {
      this.lastEvent = 'mouseDown';
    }, 300);
    this.props.fieldStore.optionsManager.ToggleShow(true);
    this.onFocus(e);
  };
}

@observer
export class SelectField extends SelectFieldBase {
  static defaultStore = StringFieldStore;
  componentDidMount() {
    super.componentDidMount();
    const { fieldStore } = this.props;
    const valDefault = fieldStore.optionsManager?.conf.default;
    if (fieldStore.isEmpty && valDefault !== undefined) {
      if (
        fieldStore.optionsManager.optionExists(
          fieldStore.conf.appKey,
          valDefault,
        ) &&
        !fieldStore.conf.autocomplete
      ) {
        let optionValue = valDefault;
        if (!!optionValue && optionValue instanceof Object) {
          optionValue = optionValue[fieldStore.conf.appKey];
        }
        const option = fieldStore.optionsManager.selectables.find(
          opt => opt[fieldStore.conf.appKey] === optionValue,
        );
        if (option) {
          fieldStore.Select(option);
        }
      }
    }
  }

  highlightByTyping = e => {
    const { target } = e;
    const optManager = this.props.fieldStore.optionsManager;
    const fs = this.props.fieldStore;
    optManager.ToggleShow(true);
    if (!fs.conf.autocomplete) {
      if (this.resetField) {
        clearTimeout(this.resetField);
      }
      this.resetField = setTimeout(() => {
        target.value = '';
      }, 500);
    }
    optManager.HighlightByPrefix(target.value);
  };

  focusNext = () => {
    const fs = this.props.fieldStore;
    if (fs.conf.highlightNextDisabled) return;
    const nextFieldSelector = `#${fs.formStore.formName}-${fs.nextFieldName}`;
    const input = document.querySelector(
      nextFieldSelector.replaceAll('=', '\\='),
    );
    if (input) {
      if (!fs.formStore.fieldStores.get(fs.nextFieldName)?.isEmpty) {
        input.blur();
      } else {
        input.focus();
      }
    } else {
      const fieldNameSelector = `#${fs.formStore.formName}-${fs.fieldName}`;
      // eslint-disable-next-line
      const input = document.querySelector(
        fieldNameSelector.replaceAll('=', '\\='),
      );
      input.blur();
    }
  };

  render() {
    const { fieldStore, responsive, noValidation } = this.props;
    // should be a dev mode only check?
    if (!fieldStore.optionsManager) {
      console.error("SelectField needs 'options' to be specified!");
    }
    const selected = fieldStore.getSelectedOption;
    const SelectionComponent = ComponentRegistry.get(
      fieldStore.conf.selectionComponent,
    );
    let selection = null;
    if (selected) {
      if (!SelectionComponent) {
        const label = objectPath.get(selected, fieldStore.conf.labelKey);
        selection = <Selected>{label}</Selected>;
      } else {
        selection = (
          <SelectionComponent selected={selected} fieldStore={fieldStore} />
        );
      }
    } else {
      selection = (
        <Placeholder>{i18next.t(fieldStore.conf.placeholder)}</Placeholder>
      );
    }
    // We have a value that represents an empty label
    // might cause a problem for other types of FieldStores than the ReferenceFieldStore?
    // effect though should be that isNullLable = false
    const isNullLabel = selected?.[fieldStore.conf.appKey] === null;
    const visible =
      (fieldStore.conf.autocomplete && fieldStore.isFocussed) || false;

    const { unclearable } = fieldStore.conf;

    return (
      <SelectWrapper
        key={fieldStore.fieldName}
        focussed={fieldStore.isFocussed}
        onMouseDown={this.onMouseDown}
        responsive={responsive}
      >
        {selection}
        {selected && !isNullLabel && !unclearable && (
          <ResetIcon
            name='close_small'
            onMouseDown={e => {
              e.stopPropagation();
            }}
            onClick={fieldStore.ResetValue}
          />
        )}
        <Arrow
          name={
            fieldStore.optionsManager.showOptions ? 'caret_up' : 'caret_down'
          }
          shiftLeft={
            !noValidation &&
            !fieldStore.conf.noValidation &&
            fieldStore.showValidation
          }
        />
        {noValidation || fieldStore.conf.noValidation ? null : (
          <Validation
            fieldStore={fieldStore}
            handleClick={this.focusAfterDelete}
          />
        )}
        {visible && <Divider lessMargin={false} />}
        <WrappedInput
          type='text'
          id={this.inputId}
          className='inputNoBorder'
          ref={input => {
            this._input = input;
          }}
          value={fieldStore?.inputValue}
          onBlur={this.onBlurSelect}
          onFocus={this.onFocusSelect}
          placeholder={fieldStore.conf?.autocomplete?.placeholder}
          posting={fieldStore.formStore.posting}
          visible={visible}
          onChange={this.highlightByTyping}
          onKeyDown={e => this.onKeyDown(e)}
          autoComplete='off'
          responsive={responsive}
        />
      </SelectWrapper>
    );
  }
}

export const SelectWrapper = styled(InputWrapper)`
  cursor: pointer;
  position: relative;
  /* to make the select wrapper disappear
  height: ${props => props.focussed && '0px'};
  margin: ${props => props.focussed && '0px'};
  padding: ${props => props.focussed && '0px'};
  overflow: ${props => props.focussed && 'hidden'}; */
  &:hover {
    ${ResetIcon} {
      opacity: 1;
    }
  }
`;
